import { I } from '@angular/cdk/keycodes';
import { Component, ElementRef, ViewChild } from '@angular/core';
import { MatStepper, MatStep, MatStepLabel } from '@angular/material/stepper';
import { Router } from '@angular/router';
import { emptyLocation, Location } from 'src/app/models/data/location';
import { ApiService } from 'src/app/services/api.services';
import {
	BannerMessageService,
	BannerMessageType,
} from 'src/app/services/banner-message.service';
import { NavigationHistoryService } from 'src/app/services/navigation-history.service';
import { AddPropertyConfirmationComponent } from './add-property-confirmation/add-property-confirmation.component';
import {
	PropertyDetailsEvent,
	PropertyType,
	EditProperty,
	emptyEditProperty,
	AddPropertyDetailsComponent,
} from './add-property-details/add-property-details.component';
import { AppNavigationHeaderComponent } from '../../components/navigation-header/app-navigation-header.component';
import { MatButton } from '@angular/material/button';

enum StepperPage {
	details = 0,
	confirmation = 1,
}

/**
 * Company id for the next Add Property visit. Kept off the URL: the company
 * Properties tab also writes `tabIndex`, and that navigation drops router state.
 */
let pendingAddPropertyCompanyId: string | undefined;

export function rememberAddPropertyCompanyId(
	companyId: string | undefined
): void {
	pendingAddPropertyCompanyId = companyId || undefined;
}

function takeAddPropertyCompanyId(): string | undefined {
	const companyId = pendingAddPropertyCompanyId;
	pendingAddPropertyCompanyId = undefined;
	return companyId;
}

@Component({
	selector: 'app-add-property',
	templateUrl: './add-property-main-tab.component.html',
	styleUrls: [
		'./add-property-main-tab.component.css',
		'../common/stepper.scss',
	],
	imports: [
		AppNavigationHeaderComponent,
		MatButton,
		MatStepper,
		MatStep,
		MatStepLabel,
		AddPropertyDetailsComponent,
		AddPropertyConfirmationComponent,
	],
})
export class AddPropertyMainTabComponent {
	editProperty: EditProperty = emptyEditProperty();
	propertyType: PropertyType = PropertyType.newProperty;

	stepper!: MatStepper;
	isEditable = false;

	serverCallInProgress = false;

	@ViewChild(AddPropertyConfirmationComponent)
	addPropertyConfirmation?: AddPropertyConfirmationComponent;
	@ViewChild(AddPropertyDetailsComponent)
	addPropertyDetails?: AddPropertyDetailsComponent;
	@ViewChild('stepperRef') stepperRef!: ElementRef;

	nextButtonLabels = {
		next: $localize`:@@addPropertyMainTab-nextButtonNext:Next`,
		confirm: $localize`:@@addPropertyMainTab-nextButtonConfirm:Confirm New Property`,
	};

	constructor(
		private readonly router: Router,
		private readonly apiService: ApiService,
		private readonly navHistory: NavigationHistoryService,
		private readonly bannerService: BannerMessageService
	) {
		this.stepper = this.stepperRef as unknown as MatStepper;
		const companyId =
			takeAddPropertyCompanyId() ??
			this.router.getCurrentNavigation?.()?.extras?.state?.['companyId'] ??
			this.router.lastSuccessfulNavigation?.extras?.state?.['companyId'];
		if (companyId) {
			this.editProperty.companyId = companyId;
		}

		if (!this.editProperty.companyId) {
			this.navHistory.navigateBack();
		}
	}

	movePrevious(stepper: MatStepper): void {
		switch (stepper.selectedIndex) {
			case StepperPage.details:
				this.navHistory.navigateBack();
				break;
			case StepperPage.confirmation:
				stepper.previous();
				break;
		}
	}

	moveNext(stepper: MatStepper): void {
		switch (stepper.selectedIndex) {
			case StepperPage.details:
				stepper.next();
				break;
			case StepperPage.confirmation:
				this.createProperty();
				break;
		}
	}

	isValid(stepper: MatStepper): boolean {
		switch (stepper.selectedIndex) {
			case StepperPage.details:
				return (
					(this.addPropertyDetails?.isValid && !!this.editProperty.timezone) ??
					false
				);
			case StepperPage.confirmation:
				return true;
		}
		return false;
	}

	getNextButtonLabel(stepper: MatStepper): string {
		switch (stepper.selectedIndex) {
			case StepperPage.confirmation:
				return this.nextButtonLabels.confirm;
			default:
				return this.nextButtonLabels.next;
		}
	}

	async createProperty(): Promise<void> {
		// make sure the user can't create the property multiple times from clicking the button multiple times quickly
		if (!this.serverCallInProgress) {
			this.serverCallInProgress = true;

			try {
				// copy all the values from the view model (EditProperty object) to the object we send back to the server (Location)
				let property: Location = emptyLocation();

				if (this.editProperty?.companyId != null) {
					property.companyId = this.editProperty.companyId;
				}

				if (this.editProperty?.name != null) {
					property.name = this.editProperty.name;
				}

				if (this.editProperty?.pendingInstalls !== null) {
					property.pendingInstalls = this.editProperty.pendingInstalls;
				}

				if (this.editProperty?.active != null) {
					property.active = this.editProperty.active;
				}

				if (this.editProperty?.address1 != null) {
					property.address.address1 = this.editProperty.address1;
				}

				if (this.editProperty?.address2 != null) {
					property.address.address2 = this.editProperty.address2;
				}

				if (this.editProperty?.locality != null) {
					property.address.locality = this.editProperty.locality;
				}

				if (this.editProperty?.countryCode != null) {
					property.address.countryCode = this.editProperty.countryCode;
				}

				if (this.editProperty?.stateProvinceRegion != null) {
					property.address.stateProvinceRegion =
						this.editProperty.stateProvinceRegion;
				}

				if (this.editProperty?.postalCode != null) {
					property.address.postalCode = this.editProperty.postalCode;
				}

				if (
					this.editProperty?.latitude != null &&
					this.editProperty?.longitude != null &&
					!isNaN(Number(this.editProperty?.latitude)) &&
					!isNaN(Number(this.editProperty?.longitude))
				) {
					property.address.geoCoordinates = {
						latitude: Number(this.editProperty.latitude),
						longitude: Number(this.editProperty.longitude),
					};
				}

				property.timezone = this.editProperty.timezone;
				property = await this.apiService.createNewProperty({ property });
				this.serverCallInProgress = false;
				this.bannerService.sendMessage(
					$localize`:@@addPropertyConfirmation-successMessage:Successfully added Property: ` +
						property.name,
					BannerMessageType.success
				);

				// take the user back to where they came from if the property was added
				this.navHistory.navigateBack();
			} catch (err) {
				this.bannerService.sendMessage(
					$localize`:@@addPropertyConfirmation-failureMessage:There was an issue while creating the Property. Please check the data and try again.`,
					BannerMessageType.error
				);
			}

			this.serverCallInProgress = true;
		}
	}
}
